import { Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { ParentalGateReason, ParentalGateRequest, ParentalGateService } from '../../services/parental-gate.service';
import { AbstractSubscriptionDestroyer } from '../../utils/abstract-subscription-destroyer';
import { SupportedLang, supportedLang, urlLang } from '../../utils/lang';

// The gate's texts are bundled here rather than in assets/i18n: it often opens during
// the first navigation (route guard), and if the translation files are late or fail to
// load the grown-up must still be able to read the question, or the child is stuck.
const TEXTS: Record<SupportedLang, {
  title: string;
  message: Record<ParentalGateReason, string>;
  question: (a: string, b: string) => string;
  wrongAnswer: string;
  cancel: string;
  continue: string;
  // Operands are shown in words so a young child can't simply read and compute the digits.
  numbers: string[];
}> = {
  en: {
    title: 'Parents only',
    message: {
      externalLink: 'This link will open outside the app. Please ask a grown-up to answer the question below.',
      parentsArea: 'This area is for parents. Please ask a grown-up to answer the question below.',
    },
    question: (a, b) => `How much is ${a} times ${b}?`,
    wrongAnswer: 'Wrong answer, please try again.',
    cancel: 'Cancel',
    continue: 'Continue',
    numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'],
  },
  fr: {
    title: 'Réservé aux parents',
    message: {
      externalLink: "Ce lien va s'ouvrir en dehors de l'application. Demande à un adulte de répondre à la question ci-dessous.",
      parentsArea: 'Cet espace est réservé aux parents. Demande à un adulte de répondre à la question ci-dessous.',
    },
    question: (a, b) => `Combien font ${a} fois ${b} ?`,
    wrongAnswer: 'Mauvaise réponse, veuillez réessayer.',
    cancel: 'Annuler',
    continue: 'Continuer',
    numbers: ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
      'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'],
  },
};

@Component({
  selector: 'wkt-parental-gate',
  templateUrl: './parental-gate.component.html',
  styleUrls: ['./parental-gate.component.scss'],
})
export class ParentalGateComponent extends AbstractSubscriptionDestroyer {
  @ViewChild('answerInput') answerInput?: ElementRef<HTMLInputElement>;

  visible = false;
  reason: ParentalGateReason = 'externalLink';
  answer = '';
  error = false;

  private a = 0;
  private b = 0;
  private resolve?: (allowed: boolean) => void;

  constructor(parentalGateService: ParentalGateService, private readonly translateService: TranslateService) {
    super();
    parentalGateService.requests$.pipe(takeUntil(this.destroyData)).subscribe((request) => this.open(request));
  }

  // Same language the rest of the page uses, resolved even before TranslateService is set up.
  get texts() {
    return TEXTS[supportedLang(this.translateService.currentLang ?? urlLang() ?? navigator.language)];
  }

  get question() {
    const { numbers, question } = this.texts;
    return question(numbers[this.a], numbers[this.b]);
  }

  submit() {
    if (Number(this.answer) === this.a * this.b) {
      this.close(true);
    } else {
      this.error = true;
      this.newQuestion();
    }
  }

  cancel() {
    this.close(false);
  }

  private open(request: ParentalGateRequest) {
    // A pending gate is denied if a new one is requested before it is answered.
    this.resolve?.(false);
    this.resolve = request.resolve;
    this.reason = request.reason;
    this.error = false;
    this.newQuestion();
    this.visible = true;
    setTimeout(() => this.answerInput?.nativeElement.focus());
  }

  private close(allowed: boolean) {
    this.visible = false;
    const resolve = this.resolve;
    this.resolve = undefined;
    resolve?.(allowed);
  }

  private newQuestion() {
    // Single digits keep it easy for a parent; below 4 is too easy for a child.
    this.a = this.randomInt(4, 9);
    this.b = this.randomInt(4, 9);
    this.answer = '';
  }

  private randomInt(min: number, max: number) {
    return min + Math.floor(Math.random() * (max - min + 1));
  }
}
